import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { normalizeNetworkList } from '../utils/socialNetworkMapper';
import { PublicationStatusBadge } from '../PublicationStatusBadge';
import {
  getArtScheduleKey,
  resolveScheduleDateTimeFields,
  buildSchedulePatchFromFields,
} from '../utils/scheduleDateTime';

export const ScheduleSelector = ({
  arts,
  artScheduleData = {},
  socialMediaSettings = {},
  onPreviewArt,
  onFinalize,
  isReadOnly = false,
  language,
}) => {
  const { t } = useTranslation();
  const [selectedArts, setSelectedArts] = useState([]);

  const handleCardClick = (art, index) => {
    if (isReadOnly) return;

    setSelectedArts([index]);
    onPreviewArt && onPreviewArt(art, index);
  };

  const handleFinalize = () => {
    if (isReadOnly) return;

    if (onFinalize && arts) {
      const mergedArts = arts.map((art, index) => {
        const artKey = getArtScheduleKey(art, index);
        const editedData = artScheduleData[artKey] || {};
        const { publishDate, publishTime } = resolveScheduleDateTimeFields(
          art,
          editedData,
        );
        const schedulePatch = buildSchedulePatchFromFields(
          publishDate,
          publishTime,
        );

        return {
          ...art,
          legend:
            editedData.caption !== undefined && editedData.caption !== ''
              ? editedData.caption
              : art.legend || '',
          originalLegend:
            editedData.originalCaption !== undefined
              ? editedData.originalCaption
              : art.legend || '',
          socialMedias: normalizeNetworkList(
            editedData.selectedNetworks !== undefined &&
              editedData.selectedNetworks.length > 0
              ? editedData.selectedNetworks
              : art.socialMedias || [],
          ),
          socialMediaSettings: {
            ...(socialMediaSettings || {}),
            ...(editedData.socialMediaSettings || {}),
            ...(art.socialMediaSettings || {}),
          },
          publicationId: art.publicationId,
          publicationStatus: art.publicationStatus,
          scheduleStatus: art.scheduleStatus,
          publicationImageUrlName: art.publicationImageUrlName,
          ...(schedulePatch || { publishDate, publishTime }),
        };
      });

      onFinalize(mergedArts);
    }
  };

  return (
    <div className={styles.scheduleCard}>
      <span className={styles.socialMediaTitle}>{t('chatsocket.schedule.publications', { lng: language })}</span>

      {arts &&
        arts.map((art, index) => {
          const isSelected = selectedArts.includes(index);
          const artKey = getArtScheduleKey(art, index);
          const scheduleData = artScheduleData[artKey] || {};
          const { publishDate, publishTime } = resolveScheduleDateTimeFields(
            art,
            scheduleData,
          );

          const dateLabel =
            publishDate || t('chatsocket.schedule.to_define', { lng: language });
          const timeLabel =
            publishTime || t('chatsocket.schedule.to_define', { lng: language });

          return (
            <div
              key={index}
              className={`${styles.scheduleOption} ${isSelected ? styles.selected : ''}`}
              onClick={() => handleCardClick(art, index)}
              style={{
                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                opacity: isReadOnly ? 0.65 : 1,
              }}
            >
              <div className={styles.scheduleOptionLeft}>
                <img
                  src={art.imageUrl}
                  alt={
                    art.piece_name ||
                    t('chatsocket.schedule.publication_fallback', { num: art.piece_number || index + 1, lng: language })
                  }
                  className={styles.scheduleImage}
                />
                <div className={styles.scheduleDetails}>
                  <span className={styles.scheduleTitle}>
                    {art.piece_name ||
                      t('chatsocket.schedule.publication_fallback', { num: art.piece_number || index + 1, lng: language })}
                  </span>
                  <div className={styles.scheduleDateTime}>
                    <div className={styles.dateTimeItem}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 2V6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 2V6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3 10H21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>
                        {dateLabel}
                      </span>
                    </div>
                    <div className={styles.dateTimeItem}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 6V12L16 14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {timeLabel}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.scheduleOptionRight}>
                {isSelected && (
                  <svg
                    className={styles.checkIcon}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}

                {art?.publicationStatus && (
                  <PublicationStatusBadge
                    status={art.publicationStatus}
                    className={styles.statusBadge}
                  />
                )}
              </div>
            </div>
          );
        })}

      <button
        className={styles.finalizarBtn}
        onClick={handleFinalize}
        disabled={isReadOnly}
      >
        {t('chatsocket.schedule.finalize', { lng: language })}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 6L9 17L4 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};
